import { FeedInManagementInfoEnum } from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { NewsModel } from '@bo-client/technician-management/desktop/news-feed/news-feed.component';
import {
  ActionTypeEnum,
  AllAreaEnum,
  CommissioningPowerMeterType
} from '@bo-client/activation-management/shared/models/commissioningModel';
import { ConnectionChangeRequestTypeEnum } from '@bo-client/shared/models/connectionChange.dto';
import { FieldConfigEnum } from '@bo-client/shared/constants/enums';
import { StorageOperationType } from '@bo-client/activation-management/grid-registration/model/storage.model';

/**
 * Prüft beim Abschluss einer Inbetriebnahme ob eine zugehörige Netzanmeldung noch keine MaStrNummern gepflegt hat.
 */
export type MaStrNummernNotwendigZumAbschlusss = boolean;
export type SteckbefestigungEHZ = 'OnePoint';
export type Dreipunktbefestigung = 'Three';
export type NichtNotwendig = 'Unnecessary';
/**
 * Eine Liste von 'Art der Befestigung' die dem Kunden im Prozess angeboten werden.
 */
export type AngeboteneArtDerBefestigung = ((SteckbefestigungEHZ | Dreipunktbefestigung | NichtNotwendig) &
  string)[];
/**
 * Der als Standard angenommen Wert für CosPhi - Kann bei der Freigabe der Netzanmeldung angegeben werden.
 */
export type VoreingestellterCosPhi = string;
/**
 *
 * Steuert, dass das Feature "Einspesierverträge" in "Stammdatenerfassung für Einspeisevergütung" umbenannt wird.
 *
 * Dem Kunden wird kein expliziter Vertrag mit Dokument angeboten, sondern lediglich ein Datenblatt um seine fehlednen Stammdaten zu erfassen.
 *
 * Der Prozess bleibt gliech. Es werden lediglich andere E-Mail-Templates und Anzeigetexte in der Oberfläche gezogen.
 */
export type EinspeisermanagementPreiskonfigurationen = {
  id?: number;
  tenant?: string;
  companyLocationExternalId?: string;
  feedInManagementInfo?:
    | 'electronicRippleControlReceiverWithProgramming'
    | 'eegBoxWithElectronicRippleControlReceiver'
    | 'feedInReduction50'
    | 'feedInReduction70'
    | 'telecontrolWithProgramming'
    | 'telecontrolWithProgramming2'
    | 'noReduction'
    | 'controlWithClsFeedInReduction60'
    | 'eegBoxWithElectronicRippleControlReceiverFI60';
  validDate?: string;
  price?: number;
  priceDescription?: string;
  priceAdditionalDescription?: string;
  [k: string]: unknown;
}[];

/**
 *
 * Steuert, dass das Feature "Einspesierverträge" in "Stammdatenerfassung für Einspeisevergütung" umbenannt wird.
 *
 * Dem Kunden wird kein expliziter Vertrag mit Dokument angeboten, sondern lediglich ein Datenblatt um seine fehlednen Stammdaten zu erfassen.
 *
 * Der Prozess bleibt gliech. Es werden lediglich andere E-Mail-Templates und Anzeigetexte in der Oberfläche gezogen.
 */
export type EinspeisermanagementOption = {
  value?: FeedInManagementInfoEnum;
  optionTitle?: string;
  optionDescription?: string;
  title?: string;
  titleDescription?: string;
  minPower: number;
  maxPower: number;
  feedInCompensation: boolean;
  [k: string]: unknown;
}[];

/**
 * Steuert, welche Messkonzepte dem Kunden angeboten werden. - Wenn  befüllt , übersteuert sie die Nutzung des standard Kataloges
 */
export type IdDesKatalogesFurMesskonzepte = string;
export type EinspeiserprozessOhneVertragsdokument = boolean;
export type Absenderadresse = string;
export type BCCAdresse = string;
export type ReplyToAdresse = string;
export type URLDerDokumentenablageOffentlich = string;
export type URLDerDokumentenablageLokal = string;
export type ArtDerPreisfindung = ArtDerPreisfindung1 & ArtDerPreisfindung2;
export type ArtDerPreisfindung1 = ExternePreisfindung | InternePreisfindung;
export type ExternePreisfindung = 'ExternalPriceCalculationService';
export type InternePreisfindung = 'CalculatePositionServiceEntryImpl';
export type ArtDerPreisfindung2 = string;
/**
 * Wenn aktiviert, wird die MwSt. aus dem Produkt aus dem Backend genommen.
 */
export type MwStAusDemBackend = boolean;
/**
 * Wenn aktiviert, wird bei einer Bestellung automatisch die Integration ins ERP angestoßen.
 */
export type AutomatischeBestellungenAktiviert = boolean;

export interface NBPConfig {
  installerPassList?: Ausweise;
  commissioning?: Inbetriebnahmen;
  gridRegistration?: Netzanmeldungen;
  feederContract?: Einspeiservertrage;
  installer?: InstallateurPortal;
  common?: Common;
  iamConfig?: IAMConfig;
  orderRequests?: Hausanschlusse;
  style?: StyleConfig;
  notifications?: NewsFeedConfig;
  connectionChangeRequest?: ConnectionChangeConfig;

  [k: string]: unknown;
}

class NewsFeedConfig {
  notifications?: NewsModel[];
  showNotificationsOnInstallerHomePage?: boolean;
}

export interface StyleConfig {
  actionButtonsBottom?: boolean;
  customCss?: string;
  cssVariables?: {
    [k: string]: string;
  };
}

export interface Ausweise {
  allowedDocumentTypes?: string;
  disableGuestPassDownload?: boolean;
  showInstallerCommitteeCheckbox?: boolean;
  disabledAreaTypes?: AllAreaEnum[];
  disablePassDownloadForAreas?: AllAreaEnum[];
  passBlacklist?: PassBlacklistModel[];

  [k: string]: unknown;
}

export interface Inbetriebnahmen {
  showConnectedObjectsCard?: boolean;
  orderCreationActive?: boolean;
  longCommentActionActive?: string;
  showNewMeterTypesForInstaller?: boolean;
  nextActionActive?: boolean;
  extendedListFilterActive?: boolean;
  showTicketSystem?: boolean;
  erpOrderIdField?: FieldConfigEnum;
  activateSubjectToFeeProcess?: boolean;
  activateTechnicalReviewProcess?: boolean;
  isHomeOwnerField?: FieldConfigEnum;
  reasonForDisconnectionField?: FieldConfigEnum;
  powerConfig?: PowerCommissioningConfig;
  gasConfig?: GasCommissioningConfig;
  waterConfig?: WaterCommissioningConfig;
  districtHeatConfig?: DistrictHeatCommissioningConfig;

  [k: string]: unknown;
}

export interface PowerCommissioningConfig {
  showNewMeterTypesForInstaller?: boolean;
  deactivateProcess?: boolean;
  checkMastrDataOnCommissioningCompletition?: MaStrNummernNotwendigZumAbschlusss;
  availablePowerMeterTypes?: CommissioningPowerMeterType[];
  availableFixingTypes?: AngeboteneArtDerBefestigung;
  defaultM1ObisCodeSingleTariffMeter?: string;
  defaultM1ObisCode?: string;
  defaultM2ObisCode?: string;
  defaultM1ObisCodeBiDirectionalMeter?: string;
  defaultM2ObisCodeBiDirectionalMeter?: string;
  defaultM1ObisCodeProducerMeter?: string;
  expectedAnnualConsumptionAlwaysRequired?: boolean;
  expectedAnnualConsumptionNeverRequired?: boolean;
  canRequestSupplyDateBeforeCompletion?: boolean;
  validateMeterInformation?: boolean;
  validateMeterInformationInstaller?: boolean;
  deactivateSupplyWishDate?: boolean;
  hideOverheadToUndergroundCabling?: boolean;
  hideTemporaryPowerCheckbox?: boolean;
  requireFixingType?: boolean;
  requireDeviceLocation?: boolean;
  externalImsysInstallationUploadsField?: FieldConfigEnum;
  externalImsysInstallationIsFeederPresentField?: FieldConfigEnum;
  externalImsysInstallationFeederTypeField?: FieldConfigEnum;
  externalImsysInstallationIsConsumerPresentField?: FieldConfigEnum;
  externalImsysInstallationConsumerTypeField?: FieldConfigEnum;
  externalImsysInstallationIsAnnualConsumptionExceededField?: FieldConfigEnum;
  transformerNumberField?: FieldConfigEnum;
  busbarSizeField?: FieldConfigEnum;
  externalImsysInstallationMeterNumberField?: FieldConfigEnum;
  externalImsysInstallationDeviceTypeField?: FieldConfigEnum;
  externalImsysInstallationMobileConnectionStrengthField?: FieldConfigEnum;
  mandatoryInstallationCaseInfo?: boolean;

  [k: string]: unknown;
}

export interface GasCommissioningConfig {
  showNewMeterTypesForInstaller?: boolean;
  showNewRegulatorTypesForInstaller?: boolean;
  deactivateProcess?: boolean;
  activateMeterDeviceInfoForInstaller?: boolean;
  activateMeterDeviceInfo?: boolean;
  installationHasChangedVisible?: boolean;
  installationHasChangedDefault?: boolean;
  hideConnectionPower?: boolean;
  loadRequired?: boolean;
  actionsNotToSendChimneySweeperMailFor?: ActionTypeEnum[];
  alwaysShowChimneySweeperMailFieldForEditor?: boolean;
  canRequestSupplyDateBeforeCompletion?: boolean;
  activateTechnicianCompletionStep?: boolean;
  hideGasMeterSealingOptionOnInstallationChange?: boolean;
  defaultM1ObisCodeSingleTariffMeter?: string;
  defaultM1ObisCode?: string;
  defaultM2ObisCode?: string;
  showTotalVolumeFlowM3H?: boolean;
  totalVolumeFlowM3HRequired?: boolean;
  requireConsumerForChangeCommunicate?: boolean;
  showSimpleMeterValue?: boolean;
  requireDeviceLocation?: boolean;
  externalGridDisconnectionCommentField?: FieldConfigEnum;
  deactivateSupplyWishDate?: boolean;
  locationField?: FieldConfigEnum;

  [k: string]: unknown;
}

export interface WaterCommissioningConfig {
  showNewMeterTypesForInstaller?: boolean;
  showIsPressureBoostingSystem?: boolean;
  showIsFireExtinguishingSystem?: boolean;
  showYearlyConsumption?: boolean;
  showDeviceReplacement?: boolean;
  deactivateProcess?: boolean;
  showOwnWaterSupply?: boolean;
  canRequestSupplyDateBeforeCompletion?: boolean;
  activateTechnicianCompletionStep?: boolean;
  showConnectionPower?: boolean;
  showTotalVolumeFlow?: boolean;
  showContinuousFlow?: boolean;
  defaultM1ObisCodeSingleTariffMeter?: string;
  defaultM1ObisCode?: string;
  defaultM2ObisCode?: string;
  hideDifferentContractPartner?: boolean;
  showProvisionalMeter?: boolean;
  requireDeviceLocation?: boolean;
  deactivateSupplyWishDate?: boolean;

  [k: string]: unknown;
}

export interface DistrictHeatCommissioningConfig {
  showNewMeterTypesForInstaller?: boolean;
  deactivateSupplyWishDate?: boolean;

  [k: string]: unknown;
}

export interface Netzanmeldungen {
  defaultCosPhi?: VoreingestellterCosPhi;
  feedInManagement?: EinspeisermanagementOption;
  feedInManagementOptions?: EinspeisermanagementPreiskonfigurationen;
  newInManagementOptionsENWG2025ActiveFromDate: string;
  meteringConceptCatalogId?: IdDesKatalogesFurMesskonzepte;
  hideAdditionalGridConnectionPointInformationCard?: boolean;
  showAssignedTransformerStation?: boolean;
  /** Aktion „Abgeschlossen Abrechnung“ in der Netzanmeldung */
  showCompletePositioning?: boolean;
  showGridConnectionPointId?: boolean;
  requireMeterReadingForCommissioningProtocol?: boolean;
  eegCompensationContact?: string;
  vde4105UpperLimit?: number;
  gridRegistrationFormVersionEnum: 'v1' | 'v2';
  allowedPowerDeviationPercent: number;
  p14aPricingTableLink?: string;
  p14aTabHint?: string;
  defaultGridConnectionPoint?: string;
  p14aRateLimitingTypeOptions?: string[];
  p14aStorageOperationOptions?: StorageOperationType[];
  hideNewPlugInProducerButton?: boolean;
  /** @deprecated */
  hideChangeMeteringConceptButton?: boolean;
  showCompensationQuestion?: boolean;
  showDirectMarketingQuery?: boolean;
  enableP14AAgbCheckbox?: boolean;
  p14AAgbCheckboxDescription: string;
  showZerezId?: boolean;
  zerezIdRequired?: boolean;
  nextActionActive?: boolean;
  extendedListFilterActive?: boolean;
  showExternalIdField?: boolean;
  showExternalCustomerIdField?: boolean;
  showTicketSystem?: boolean;
  /*showFeedInManagementForPlaceInServiceRequest?: boolean;*/
  changeMeteringConcept?: ChangeMeteringConceptConfig;
  mastrValidationConfig?: MastrValidationConfig;
  disabledMeteringConcepts?: (string | number)[];
  maxValidityRenewalCount?: number;
  chargerConfig?: ChargerConfig;
  storageConfig?: StorageConfig;
  consumerConfig?: ConsumerConfig;

  [k: string]: unknown;
}

export interface Einspeiservertrage {
  withoutExplicitContractDocument?: EinspeiserprozessOhneVertragsdokument;
  sepaActivated?: boolean;
  showAdditionalMastrFields?: boolean;
  showArchiveAction?: boolean;
  companyInDistressCheck?: boolean;
  showTicketSystem?: boolean;
  feederContractCreationAfterCommissioning?: boolean;
  showFullFeedInBonusProcess?: boolean;

  [k: string]: unknown;
}

export interface InstallateurPortal {
  [k: string]: unknown;
}

export interface Common {
  senderAddress?: Absenderadresse;
  bccAddress?: BCCAdresse;
  replyToAddress?: ReplyToAdresse;
  documentStorageLocation?: URLDerDokumentenablageOffentlich;
  localStorageLocation?: URLDerDokumentenablageLokal;
  offerConfirmationUrl?: string;
  conversationClientUrl?: string;
  billingBkr?: string;
  billingExtId?: string;
  billingIntegrationEndpoint?: string;
  billingAuthHeader?: string;
  ewBillingIntegrationEndpoint?: string;
  ewBillingAuthHeader?: string;
  pmTenant?: string;
  pmIntegrationEndpoint?: string;
  pmPassword?: string;
  pmUser?: string;
  hostName?: string;
  backofficeUrl?: string;
  mailSettings?: {
    id?: number;
    templates?: {
      id?: number;
      htmlContent?: string;
      type?:
        | 'DEFAULT'
        | 'OFFER_REQUEST_CONFIRMATION'
        | 'ORDER_CONFIRMATION'
        | 'ORDER_CONFIRMATION_REQUEST'
        | 'ORDER_CONFIRMATION_LETTER'
        | 'ORDER_REQUEST_LETTER'
        | 'BREAKUP_NOTICE'
        | 'CANCELLATION_LETTER'
        | 'NEW_MESSAGE_NOTIFICATION'
        | 'BUYING_CONTRACT'
        | 'MAIL_CONFIRMATION_REQUEST'
        | 'PASSWORD_CHANGE_REQUEST'
        | 'TM_WELLCOME_ADMIN_INSTALLATEUR'
        | 'BUILDING_REPORT'
        | 'TM_PASS_REJECTED'
        | 'TM_INSTALLER_PASS'
        | 'ORDER_CONFIRMATION_DELETE'
        | 'COM_BBS_APPROVAL'
        | 'COM_VALID'
        | 'COM_SUPPLY_DATE_APPOINTMENT'
        | 'COM_CHANGE_SUPPLY_APPOINTMENT'
        | 'TM_PASS_VALID'
        | 'TM_PASS_RENEWED'
        | 'TM_GRID_REGISTRATION_VALID'
        | 'TM_GRID_REGISTRATION_DECLINED'
        | 'TM_GRID_REGISTRATION_REQUEST_VALIDATION'
        | 'TM_GR_REQUESTED_CUSTOMER_INFO'
        | 'TM_CM_CHIMNEYSWEEP_INFO'
        | 'MAIL_FOOTER'
        | 'BUILDING_DOSSIER'
        | 'COM_REQUESTED'
        | 'COM_INFO_SHEET'
        | 'TM_GRID_REGISTRATION_FEEDBACK_NEEDED'
        | 'TM_GRID_REGISTRATION_INFO_SHEET'
        | 'COM_CLOSED'
        | 'COM_FEEDBACK_NEEDED'
        | 'FEEDER_CONTRACT'
        | 'FEEDER_CONTRACT_CONFIRMED'
        | 'FEEDER_CONTRACT_CONFIRMATION_REQUEST'
        | 'COM_CHIMNEY_SWEEP_INFO'
        | 'CUSTOMER_CSS'
        | 'COM_CHIMNEYSWEEP_REQUESTED'
        | 'COM_CHIMNEYSWEEP_CONFIRMED'
        | 'FEEDER_CONTRACT_ACCEPTED'
        | 'FEEDER_CONTRACT_FEEDBACK_NEEDED'
        | 'DATA_SHEET_HA_GASWATER'
        | 'GR_PLACE_IN_SERVICE_REQUESTED'
        | 'GR_PLACE_IN_SERVICE_CONFIRMED'
        | 'FEEDER_CONTRACT_CONFIRMATION_REQUEST_INFO_SHEET'
        | 'OR_REQUEST_APPROVAL'
        | 'TM_GRID_REGISTRATION_REQUEST_ORDER'
        | 'TM_GRID_REGISTRATION_ORDER_CONFIRMATION'
        | 'TM_GRID_REGISTRATION_FEED_IN_READY'
        | 'ORDER_REVOCATION'
        | 'ORDER_REVOCATION_CONFIRMATION'
        | 'ORDER_CONFIRMATION_INTRO'
        | 'OFFER_REQUEST_INTRO'
        | 'ORDER_REQUEST_FELIX_PROJECT'
        | 'CONSTRUCTION_FILE'
        | 'ORDER_REQUEST_CANCELED'
        | 'ORDER_REQUEST_INTERN_INFO_CANCELED'
        | 'TM_GRID_REGISTRATION_PENDING_ASSIGN_TECHNICIAN'
        | 'TM_GRID_REGISTRATION_ASSIGNED_TO_TECHNICIAN'
        | 'COM_CHIMNEYSWEEP_REJECTED'
        | 'COMMISSIONING_CHECK'
        | 'PLUGIN_GRID_REGISTRATION_RECEIVED'
        | 'PLUGIN_GRID_REGISTRATION_DECLINED'
        | 'PLUGIN_GRID_REGISTRATION_APPROVED_METER'
        | 'PLUGIN_GRID_REGISTRATION_APPROVED_NO_METER'
        | 'PLUGIN_GRID_REGISTRATION_FEEDBACK_NEEDED'
        | 'TM_GRID_REGISTRATION_SHARE_TO_TECHNICIAN_REQUEST'
        | 'TM_GRID_REGISTRATION_SHARE_TO_TECHNICIAN_ACCEPT'
        | 'COM_CORRECT'
        | 'TM_GRID_REGISTRATION_CORRECT'
        | 'PV_REGISTRATION_FORM'
        | 'TM_PASS_FEEDBACK_NEEDED'
        | 'ORDER_REQUEST_APPROVED'
        | 'FC_CONFIRMATION_WITHOUT_CONTRACT'
        | 'FC_CONFIRMATION_WITHOUT_CONTRACT_INFO_SHEET'
        | 'FEEDER_WITHOUT_CONTRACT_ACCEPTED'
        | 'TM_GRID_REGISTRATION_REMINDER'
        | 'TM_GRID_REGISTRATION_SEND_TO_REVIEW';
      format?: 'HTML_THYMELEAF' | 'DOCX_STAMPER' | 'FAST_REPORT' | 'FILLABLE_PDF';
      documentContent?: {
        id?: number;
        displayName?: string;
        cokDocId?: string;
        fileName?: string;
        url?: string;
        tag?: string;
        displayToCustomer?: boolean;
        transfered?: boolean;
        relatedToArea?: string;
        [k: string]: unknown;
      };
      [k: string]: unknown;
    }[];
    [k: string]: unknown;
  };
  theme?: {
    id?: number;
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    tertiaryColor?: string;
    [k: string]: unknown;
  };
  lastSapStateSyncDateTime?: string;
  sendMailByDeleteOrderRequest?: boolean;
  deleteAddress?: string;
  supplyAppointmentDays?: number;
  chimneySweepFeedbackMail?: string;
  mailSubjectPattern?: string;
  feederContractQuestionsMail?: string;
  defaultTechnicianId?: number;
  privacyPolicy?: string;
  privacyPolicyUrl?: string;
  termsAndConditionsUrl?: string;
  termsAndConditions?: string;
  hideActivateLogin?: boolean;
  useAvailabilityEndpointForAreas?: boolean;
  disableInstallerDeletion?: boolean;

  [k: string]: unknown;
}

export interface Hausanschlusse {
  orderRequestDetailPageVersion?: string;
  priceCalculatorType?: ArtDerPreisfindung;
  mwstFromBackend?: MwStAusDemBackend;
  canOrderAutomatically?: AutomatischeBestellungenAktiviert;
  onlineNAVGridConnectionContract?: boolean;
  onlineNAVMSGridConnectionContract?: boolean;
  onlineNDAVGasGridConnectionContract?: boolean;
  additionalValidationBeforeOrderCreation?: boolean;
  showTicketSystem?: boolean;

  [k: string]: unknown;
}

export interface IAMConfig {
  redirectUrlSuffix?: string;
  hideLocalLogin?: boolean;
  externalIdpEnabled?: boolean;
  authorityUrl?: string;
  authWellknownEndpointUrl?: string;
  loginFlowId?: string;
  clientId?: string;
  clientSecret?: string;
  scope?: string;
  loginWithButtonText?: string;
  loginWithDescription?: string;
  /** Azure B2C Tenant-Name für Impersonation (z. B. "contosob2c") */
  b2cTenantName?: string;
  /** Client-ID der Cockpit-App-Registrierung in Azure B2C */
  cockpitB2cClientId?: string;
  /** Redirect-URI der Cockpit-App nach erfolgreichem Impersonation-Login */
  cockpitRedirectUri?: string;
}

export interface ConnectionChangeConfig {
  isActive?: boolean;
  showTicketSystem?: boolean;
  showPriceCalculationExternalId?: boolean;
  connectionChangeRequestTypes?: ConnectionChangeRequestTypeConfig[];
}

export class ConnectionChangeRequestTypeConfig {
  area?: AllAreaEnum;
  connectionChangeRequestType?: ConnectionChangeRequestTypeEnum;
  isActive?: boolean;
  introText?: string;
  defaultPowerUnit?: string;
  deviceNumberField?: FieldConfigEnum;
  ownershipVerificationField?: FieldConfigEnum;
  explosiveField?: FieldConfigEnum;
  lineChangeCommentField?: FieldConfigEnum;
  supplyAppointmentWishDateField?: FieldConfigEnum;
  /* Required-Values für differingInvoiceContactField, powerIncreaseField, moveServiceEntranceBoxField, moveConnectionLineField werden erst mal ignoriert
      -> Macht wenig Sinn die verpflichtend anzuhaken */
  differingInvoiceContactField?: FieldConfigEnum;
  powerIncreaseField?: FieldConfigEnum;
  moveServiceEntranceBoxField?: FieldConfigEnum;
  moveConnectionLineField?: FieldConfigEnum;
  requiredPowerFieldInTemporary?: FieldConfigEnum;
  offerCheckBoxes?: { checkBoxText: string }[];

  static getConnectionChangeRequestTypeConfig(
    area: AllAreaEnum,
    type: ConnectionChangeRequestTypeEnum,
    connectionChangeRequestTypes: ConnectionChangeRequestTypeConfig[]
  ): ConnectionChangeRequestTypeConfig | undefined {
    if (!connectionChangeRequestTypes || !Array.isArray(connectionChangeRequestTypes) || !area || !type) {
      return undefined;
    }
    return connectionChangeRequestTypes
      .filter(
        (config) => !!config && config.isActive && !!config.area && !!config.connectionChangeRequestType
      )
      .find(
        (config) =>
          config.area.toUpperCase() === area?.toUpperCase() &&
          config.connectionChangeRequestType.toUpperCase() === type?.toUpperCase()
      );
  }

  [k: string]: unknown;
}

export class PassBlacklistModel {
  areas: AllAreaEnum[];
  companyLocations: string[];
  licenseDeactivated: boolean;
  passDeactivated: boolean;
}

export class ChangeMeteringConceptConfig {
  hideChangeMeteringConceptButton?: boolean;
  commissioningDateField?: FieldConfigEnum;
  newInstalledPowerField?: FieldConfigEnum;

  [k: string]: unknown;
}

export class MastrValidationConfig {
  enableMastrValidationForPlaceInServiceRequest?: boolean;
  allowedPowerDeviation?: string;
  allowedCapacityDeviation?: string;
  allowedModuleCountDeviation?: string;
  allowValidationDeactivation?: boolean;
  enableStrictAddressValidation?: boolean;
  allowPlaceInServiceDateEdit?: boolean;

  [k: string]: unknown;
}

export class ChargerConfig {
  chargerTypeField?: FieldConfigEnum;

  [k: string]: unknown;
}

export class StorageConfig {
  enableNewStorageProcess?: boolean;

  [k: string]: unknown;
}

export class ConsumerConfig {
  maLoField?: FieldConfigEnum;
}
